import React, { useState, useMemo } from 'react';
import {
    Search,
    X,
    Book,
    Settings,
    Wrench,
    Lightbulb,
    ChevronDown,
    ChevronRight,
    ExternalLink,
} from 'lucide-react';
import {
    helpData,
    searchHelp,
    helpCategories,
    helpSubcategories,
    HelpItem,
} from './utils/helpData';

interface HelpPaneProps {
    isOpen: boolean;
    onClose: () => void;
}

const categoryIcons = {
    tool: Wrench,
    setting: Settings,
    concept: Lightbulb,
};

const categoryColors = {
    tool: 'text-blue-600 bg-blue-50 border-blue-200',
    setting: 'text-green-600 bg-green-50 border-green-200',
    concept: 'text-purple-600 bg-purple-50 border-purple-200',
};

export default function HelpPane({ isOpen, onClose }: HelpPaneProps) {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
        new Set(['tool', 'setting', 'concept']),
    );

    // Filter and search help items
    const filteredItems = useMemo(() => {
        let items = searchQuery ? searchHelp(searchQuery) : helpData;

        if (selectedCategory !== 'all') {
            items = items.filter((item) => item.category === selectedCategory);
        }

        return items;
    }, [searchQuery, selectedCategory]);

    // Group items by category and subcategory
    const groupedItems = useMemo(() => {
        const groups: {
            [category: string]: { [subcategory: string]: HelpItem[] };
        } = {};

        filteredItems.forEach((item) => {
            if (!groups[item.category]) {
                groups[item.category] = {};
            }

            const subcategory = item.subcategory || 'general';
            if (!groups[item.category][subcategory]) {
                groups[item.category][subcategory] = [];
            }

            groups[item.category][subcategory].push(item);
        });

        return groups;
    }, [filteredItems]);

    const toggleItemExpansion = (itemId: string) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedItems(newExpanded);
    };

    const toggleCategoryExpansion = (category: string) => {
        const newExpanded = new Set(expandedCategories);
        if (newExpanded.has(category)) {
            newExpanded.delete(category);
        } else {
            newExpanded.add(category);
        }
        setExpandedCategories(newExpanded);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-5/6 flex flex-col mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <Book className="w-6 h-6 text-blue-600" />
                        <h2 className="text-xl font-semibold text-gray-900">
                            Experience Builder Help
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-md hover:bg-gray-100 transition-colors"
                        title="Close Help"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Search and Filters */}
                <div className="p-6 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row gap-4">
                        {/* Search Input */}
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search help topics..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        {/* Category Filter */}
                        <div className="sm:w-48">
                            <select
                                value={selectedCategory}
                                onChange={(e) =>
                                    setSelectedCategory(e.target.value)
                                }
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="all">All Categories</option>
                                {Object.entries(helpCategories).map(
                                    ([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ),
                                )}
                            </select>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="mt-3 text-sm text-gray-600">
                        {filteredItems.length} help{' '}
                        {filteredItems.length === 1 ? 'topic' : 'topics'} found
                    </div>
                </div>

                {/* Help Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {filteredItems.length === 0 ? (
                        <div className="text-center py-12">
                            <Book className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                No help topics found
                            </h3>
                            <p className="text-gray-500">
                                Try adjusting your search terms or category
                                filter.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {Object.entries(groupedItems).map(
                                ([category, subcategories]) => {
                                    const CategoryIcon =
                                        categoryIcons[
                                            category as keyof typeof categoryIcons
                                        ];
                                    const isExpanded =
                                        expandedCategories.has(category);

                                    return (
                                        <div
                                            key={category}
                                            className="border border-gray-200 rounded-lg"
                                        >
                                            {/* Category Header */}
                                            <button
                                                onClick={() =>
                                                    toggleCategoryExpansion(
                                                        category,
                                                    )
                                                }
                                                className="w-full flex items-center p-4 text-left hover:bg-gray-50 transition-colors"
                                                style={{
                                                    width: 'calc(100% - 2rem)',
                                                }}
                                            >
                                                <CategoryIcon className="w-5 h-5 text-gray-600 mr-3 flex-shrink-0" />
                                                <h3 className="text-lg font-semibold text-gray-900 flex-grow mr-3">
                                                    {
                                                        helpCategories[
                                                            category as keyof typeof helpCategories
                                                        ]
                                                    }
                                                </h3>
                                                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full mr-3 flex-shrink-0">
                                                    {
                                                        Object.values(
                                                            subcategories,
                                                        ).flat().length
                                                    }
                                                </span>
                                                {isExpanded ? (
                                                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                ) : (
                                                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                                )}
                                            </button>

                                            {/* Category Content */}
                                            {isExpanded && (
                                                <div className="border-t border-gray-200">
                                                    {Object.entries(
                                                        subcategories,
                                                    ).map(
                                                        ([
                                                            subcategory,
                                                            items,
                                                        ]) => (
                                                            <div
                                                                key={
                                                                    subcategory
                                                                }
                                                                className="p-4 border-b border-gray-100 last:border-b-0"
                                                            >
                                                                {/* Subcategory Header */}
                                                                {subcategory !==
                                                                    'general' && (
                                                                    <h4 className="text-sm font-medium text-gray-700 mb-3 uppercase tracking-wide">
                                                                        {helpSubcategories[
                                                                            subcategory as keyof typeof helpSubcategories
                                                                        ] ||
                                                                            subcategory}
                                                                    </h4>
                                                                )}

                                                                {/* Help Items */}
                                                                <div className="space-y-3">
                                                                    {items.map(
                                                                        (
                                                                            item,
                                                                        ) => {
                                                                            const isItemExpanded =
                                                                                expandedItems.has(
                                                                                    item.id,
                                                                                );

                                                                            return (
                                                                                <div
                                                                                    key={
                                                                                        item.id
                                                                                    }
                                                                                    className="border border-gray-200 rounded-md"
                                                                                >
                                                                                    {/* Item Header */}
                                                                                    <button
                                                                                        onClick={() =>
                                                                                            toggleItemExpansion(
                                                                                                item.id,
                                                                                            )
                                                                                        }
                                                                                        className="w-full flex items-center p-3 text-left hover:bg-gray-50 transition-colors"
                                                                                        style={{
                                                                                            width: 'calc(100% - 1.5rem)',
                                                                                        }}
                                                                                    >
                                                                                        <div
                                                                                            className={`w-2 h-2 rounded-full mr-3 flex-shrink-0 ${
                                                                                                categoryColors[
                                                                                                    item
                                                                                                        .category
                                                                                                ].split(
                                                                                                    ' ',
                                                                                                )[2]
                                                                                            }`}
                                                                                        />
                                                                                        <h5 className="font-medium text-gray-900 flex-grow mr-3">
                                                                                            {
                                                                                                item.title
                                                                                            }
                                                                                        </h5>
                                                                                        {isItemExpanded ? (
                                                                                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                                        ) : (
                                                                                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                                                                        )}
                                                                                    </button>

                                                                                    {/* Item Content */}
                                                                                    {isItemExpanded && (
                                                                                        <div className="px-3 pb-3 border-t border-gray-100">
                                                                                            <div className="pt-3 space-y-4">
                                                                                                {/* Description */}
                                                                                                <div>
                                                                                                    <p className="text-gray-700 leading-relaxed">
                                                                                                        {
                                                                                                            item.description
                                                                                                        }
                                                                                                    </p>
                                                                                                </div>

                                                                                                {/* Usage */}
                                                                                                {item.usage && (
                                                                                                    <div>
                                                                                                        <h6 className="text-sm font-medium text-gray-900 mb-2">
                                                                                                            How
                                                                                                            to
                                                                                                            use:
                                                                                                        </h6>
                                                                                                        <p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-md">
                                                                                                            {
                                                                                                                item.usage
                                                                                                            }
                                                                                                        </p>
                                                                                                    </div>
                                                                                                )}

                                                                                                {/* Examples */}
                                                                                                {item.examples &&
                                                                                                    item
                                                                                                        .examples
                                                                                                        .length >
                                                                                                        0 && (
                                                                                                        <div>
                                                                                                            <h6 className="text-sm font-medium text-gray-900 mb-2">
                                                                                                                Examples:
                                                                                                            </h6>
                                                                                                            <ul className="space-y-1">
                                                                                                                {item.examples.map(
                                                                                                                    (
                                                                                                                        example,
                                                                                                                        index,
                                                                                                                    ) => (
                                                                                                                        <li
                                                                                                                            key={
                                                                                                                                index
                                                                                                                            }
                                                                                                                            className="text-sm text-gray-600 flex items-start gap-2"
                                                                                                                        >
                                                                                                                            <span className="text-gray-400 mt-1">
                                                                                                                                •
                                                                                                                            </span>
                                                                                                                            <span>
                                                                                                                                {
                                                                                                                                    example
                                                                                                                                }
                                                                                                                            </span>
                                                                                                                        </li>
                                                                                                                    ),
                                                                                                                )}
                                                                                                            </ul>
                                                                                                        </div>
                                                                                                    )}

                                                                                                {/* Related Items */}
                                                                                                {item.relatedItems &&
                                                                                                    item
                                                                                                        .relatedItems
                                                                                                        .length >
                                                                                                        0 && (
                                                                                                        <div>
                                                                                                            <h6 className="text-sm font-medium text-gray-900 mb-2">
                                                                                                                Related
                                                                                                                topics:
                                                                                                            </h6>
                                                                                                            <div className="flex flex-wrap gap-2">
                                                                                                                {item.relatedItems.map(
                                                                                                                    (
                                                                                                                        relatedId,
                                                                                                                    ) => (
                                                                                                                        <button
                                                                                                                            key={
                                                                                                                                relatedId
                                                                                                                            }
                                                                                                                            onClick={() => {
                                                                                                                                setSearchQuery(
                                                                                                                                    '',
                                                                                                                                );
                                                                                                                                setSelectedCategory(
                                                                                                                                    'all',
                                                                                                                                );
                                                                                                                                toggleItemExpansion(
                                                                                                                                    relatedId,
                                                                                                                                );
                                                                                                                            }}
                                                                                                                            className="text-xs text-blue-600 hover:text-blue-800 bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded-full transition-colors flex items-center gap-1"
                                                                                                                        >
                                                                                                                            <span>
                                                                                                                                {relatedId.replace(
                                                                                                                                    /-/g,
                                                                                                                                    ' ',
                                                                                                                                )}
                                                                                                                            </span>
                                                                                                                            <ExternalLink className="w-3 h-3" />
                                                                                                                        </button>
                                                                                                                    ),
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    )}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        },
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ),
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
